import { useCallback, useEffect, useMemo } from 'react'
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'

import type { TaskService, UserService } from '@duck/api-definitions'

import { apis } from './apiClients'

const systemUser: UserService.User = { id: 'system', firstName: 'System', lastName: '', email: 'system@system.com', role: 'admin', isActive: true, isVerified: true, accessGroups: [], createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000

type CacheEntry<T> = {
  data: T | null
  timestamp: number
}

type CacheStore<T> = Record<string, CacheEntry<T>>

const createCache = <T>() => {
  let memoryCache: CacheStore<T> = {}

  const get = (id: string): CacheEntry<T> | null => {
    const entry = memoryCache[id]
    if (!entry) {
      return null
    }

    if (Date.now() - entry.timestamp > CACHE_MAX_AGE_MS) {
      const { [id]: _, ...rest } = memoryCache
      memoryCache = rest
      return null
    }

    return entry
  }

  const set = (id: string, data: T | null) => {
    memoryCache[id] = {
      data,
      timestamp: Date.now()
    }
  }

  return { get, set }
}

const userCache = createCache<UserService.User | null>()
const taskCache = createCache<TaskService.Task | null>()

const userQueryKeys = {
  all: ['users'] as const,
  detail: (id: string) => ['users', 'detail', id] as const
}

const taskQueryKeys = {
  all: ['tasks'] as const,
  detail: (id: string) => ['tasks', 'detail', id] as const
}

const fetchUserById = async (id: string) => {
  try {
    if (id === 'system') {
      return systemUser
    }
    const response = await apis.userService.idGet(id)
    return response.data
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return null
    }
    throw error
  }
}

const fetchTaskById = async (id: string) => {
  try {
    const response = await apis.taskService.idGet(id)
    return response.data
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return null
    }
    throw error
  }
}

const computeUserDisplayName = (user: UserService.User | null, fallback: string) => {
  if (!user) {
    return fallback
  }

  const trimmedFirst = user.firstName?.trim()
  const trimmedLast = user.lastName?.trim()
  const fullName = [trimmedFirst, trimmedLast].filter(Boolean).join(' ').trim()

  if (fullName.length > 0) {
    return fullName
  }

  if (user.email?.trim()) {
    return user.email.trim()
  }

  return fallback
}

const computeTaskDisplayName = (task: TaskService.Task | null, fallback: string) => {
  if (!task) {
    return fallback
  }

  if (task.name?.trim()) {
    return task.name.trim()
  }

  return fallback
}

const normalizeIds = (ids: Array<string | null | undefined>) =>
  Array.from(
    new Set(
      ids
        .map((value) => value?.trim())
        .filter((value): value is string => Boolean(value && value.length > 0))
    )
  )

type UseDisplayNameOptions = {
  fallback?: string
}

export const useUserDisplayName = (
  userId: string | null | undefined,
  options?: UseDisplayNameOptions
) => {
  const id = userId?.trim()
  const fallback = options?.fallback ?? (id ?? 'Unknown user')
  const cachedEntry = useMemo(() => (id ? userCache.get(id) : null), [id])

  const query = useQuery({
    queryKey: id ? userQueryKeys.detail(id) : userQueryKeys.all,
    queryFn: () => (id ? fetchUserById(id) : Promise.resolve(null)),
    enabled: Boolean(id),
    staleTime: 5 * 60 * 1000,
    initialData: cachedEntry?.data ?? undefined,
    initialDataUpdatedAt: cachedEntry?.timestamp
  })

  const displayName = computeUserDisplayName(query.data ?? null, fallback)

  useEffect(() => {
    if (!id) {
      return
    }
    if (query.data !== undefined) {
      userCache.set(id, query.data ?? null)
    }
  }, [id, query.data])

  return {
    displayName,
    user: query.data,
    isLoading: query.isPending,
    isError: query.isError
  }
}

export const useTaskDisplayName = (
  taskId: string | null | undefined,
  options?: UseDisplayNameOptions
) => {
  const id = taskId?.trim()
  const fallback = options?.fallback ?? (id ?? 'Unknown task')
  const cachedEntry = useMemo(() => (id ? taskCache.get(id) : null), [id])

  const query = useQuery({
    queryKey: id ? taskQueryKeys.detail(id) : taskQueryKeys.all,
    queryFn: () => (id ? fetchTaskById(id) : Promise.resolve(null)),
    enabled: Boolean(id),
    staleTime: 5 * 60 * 1000,
    initialData: cachedEntry?.data ?? undefined,
    initialDataUpdatedAt: cachedEntry?.timestamp
  })

  const displayName = computeTaskDisplayName(query.data ?? null, fallback)

  useEffect(() => {
    if (!id) {
      return
    }
    if (query.data !== undefined) {
      taskCache.set(id, query.data ?? null)
    }
  }, [id, query.data])

  return {
    displayName,
    task: query.data,
    isLoading: query.isPending,
    isError: query.isError
  }
}

export const useTaskDisplayNames = (
  taskIds: Array<string | null | undefined>,
  options?: UseDisplayNameOptions
) => {
  const fallback = options?.fallback ?? 'Unknown task'
  const normalizedIds = useMemo(() => normalizeIds(taskIds), [taskIds])

  const queries = useQueries({
    queries: normalizedIds.map((id) => {
      const cachedEntry = taskCache.get(id)
      return {
        queryKey: taskQueryKeys.detail(id),
        queryFn: () => fetchTaskById(id),
        enabled: Boolean(id),
        staleTime: 5 * 60 * 1000,
        initialData: cachedEntry?.data ?? undefined,
        initialDataUpdatedAt: cachedEntry?.timestamp
      }
    })
  })

  // Update cache when queries complete
  useEffect(() => {
    queries.forEach((q, index) => {
      const id = normalizedIds[index]
      if (id && q.data !== undefined) {
        taskCache.set(id, q.data ?? null)
      }
    })
  }, [queries, normalizedIds])

  const getDisplayName = useCallback((taskId: string) => {
    const index = normalizedIds.indexOf(taskId)
    if (index === -1) {
      const cacheData = taskCache.get(taskId)?.data ?? null
      return computeTaskDisplayName(cacheData, fallback)
    }
    const task = queries[index]?.data ?? null
    return computeTaskDisplayName(task, fallback)
  }, [queries, normalizedIds, fallback])

  const isLoading = queries.some(q => q.isPending)
  const isError = queries.some(q => q.isError)

  return {
    getDisplayName,
    isLoading,
    isError
  }
}

export const usePrefetchUsers = () => {
  const queryClient = useQueryClient()

  return useCallback(
    async (ids: Array<string | null | undefined>) => {
      const uniqueIds = normalizeIds(ids)

      await Promise.all(
        uniqueIds.map(async (id) => {
          await queryClient.prefetchQuery({
            queryKey: userQueryKeys.detail(id),
            queryFn: () => fetchUserById(id),
            staleTime: 5 * 60 * 1000
          })
          const data = queryClient.getQueryData<UserService.User | null>(userQueryKeys.detail(id))
          if (data !== undefined) {
            userCache.set(id, data ?? null)
          }
        })
      )
    },
    [queryClient]
  )
}

export const usePrefetchTasks = () => {
  const queryClient = useQueryClient()

  return useCallback(
    async (ids: Array<string | null | undefined>) => {
      const uniqueIds = normalizeIds(ids)

      await Promise.all(
        uniqueIds.map(async (id) => {
          await queryClient.prefetchQuery({
            queryKey: taskQueryKeys.detail(id),
            queryFn: () => fetchTaskById(id),
            staleTime: 5 * 60 * 1000
          })
          const data = queryClient.getQueryData<TaskService.Task | null>(taskQueryKeys.detail(id))
          if (data !== undefined) {
            taskCache.set(id, data ?? null)
          }
        })
      )
    },
    [queryClient]
  )
}

export const entityLookupKeys = {
  users: userQueryKeys,
  tasks: taskQueryKeys
}


